import { CookieSetOptions, CookieGetOptions } from 'universal-cookie';
import cookieService from 'utils/cookieService';
import storageDefines from 'constants/models/storageDefines';
import { storageItem } from 'constants/models/storageItem';
import { storageKeys } from 'constants/enums/storageKeys.enum';
import { storageTypes } from 'constants/enums/storageTypes.enum';
import AES from 'crypto-js/aes';
import encUtf8 from 'crypto-js/enc-utf8';

/**
 * @description (源碼掃描) 針對緩存內容加密
 * @param value 明碼資料
 */
const encrypt = (value: string | null) => {
  if (!value) return '';
  return AES.encrypt(value, 'wwunion storage protection').toString();
};

/**
 * @description (源碼掃描) 針對加密資料解密
 * @param value 加密資料
 */
const decrypt = (value: string | null) => {
  if (!value) return '';
  const bytes = AES.decrypt(value, 'wwunion storage protection');
  return bytes.toString(encUtf8);
};

/**
 * @description (源碼掃描) 設定儲存值，僅限白名單鍵值方可存取
 */
const setItem = (key: storageKeys, value: string, options?: CookieSetOptions): void => {
  const whiteList = Object.values(storageKeys);
  const allowKey = whiteList.find(it => it === key);
  if (allowKey) {
    switch (storageDefines[allowKey]) {
      case storageTypes.local:
        localStorage.setItem(allowKey, encrypt(value));
        break;
      case storageTypes.session:
        sessionStorage.setItem(allowKey, encrypt(value));
        break;
      case storageTypes.cookies:
        cookieService.instance.set(allowKey, encrypt(value), options);
        break;
    }
  }
};

/**
 * @description (源碼掃描) 取得儲存值，僅限白名單鍵值方可存取
 */
const getItem = (key: storageKeys, options?: CookieGetOptions): any => {
  const whiteList = Object.values(storageKeys);
  let retVal = null;
  const allowKey = whiteList.find(it => it === key);
  if (allowKey) {
    switch (storageDefines[allowKey]) {
      case storageTypes.local: {
        const content = localStorage.getItem(allowKey);
        try {
          const _data = decrypt(content);
          if (_data) retVal = _data;
        } catch (err) {
          // 若解碼出錯，則清除原資料(可能為舊版的明碼資料，故無法解密)
          localStorage.removeItem(allowKey);
        }
        break;
      }
      case storageTypes.session: {
        const content = sessionStorage.getItem(allowKey);
        try {
          const _data = decrypt(content);
          if (_data) retVal = _data;
        } catch (err) {
          // 若解碼出錯，則清除原資料(可能為舊版的明碼資料，故無法解密)
          sessionStorage.removeItem(allowKey);
        }
        break;
      }
      case storageTypes.cookies: {
        const content = cookieService.instance.get(allowKey, options);
        try {
          const _data = decrypt(content);
          if (_data) retVal = _data;
        } catch (err) {
          // 若解碼出錯，則清除原資料(可能為舊版的明碼資料，故無法解密)
          cookieService.instance.remove(allowKey);
        }
        break;
      }
    }
  }
  return retVal;
};

/**
 * @description 移除儲存值
 */
const removeItem = (key: storageKeys, options?: CookieSetOptions): void => {
  const whiteList = Object.values(storageKeys);
  const allowKey = whiteList.find(it => it === key);
  if (allowKey) {
    switch (storageDefines[allowKey]) {
      case storageTypes.local:
        localStorage.removeItem(allowKey);
        break;
      case storageTypes.session:
        sessionStorage.removeItem(allowKey);
        break;
      case storageTypes.cookies:
        cookieService.instance.remove(allowKey, options);
        break;
    }
  }
};

/**
 * @description 取得全部儲存值
 */
const getAll = {
  local: (): storageItem[] => {
    return Object.keys(localStorage)
      .map(k => ({ key: k, value: decrypt(localStorage[k]), type: storageTypes.local, length: localStorage[k].length }));
  },
  session: (): storageItem[] => {
    return Object.keys(sessionStorage)
      .map(k => ({ key: k, value: decrypt(sessionStorage[k]), type: storageTypes.session, length: sessionStorage[k].length }));
  },
  cookies: (): storageItem[] => {
    return Object.keys(cookieService.instance.getAll())
      .map(k => ({ key: k, value: decrypt(cookieService.instance.get(k)), type: storageTypes.cookies, length: cookieService.instance.get(k).length }));
  },
  all: (): storageItem[] => {
    const locals = getAll.local();
    const sessions = getAll.session();
    const cookies = getAll.cookies();
    return locals.concat(sessions).concat(cookies);
  }
};

/**
 * @description 清除全部儲存值
 */
const clearAll = {
  local: () => {
    localStorage.clear();
  },
  session: () => {
    sessionStorage.clear();
  },
  cookies: () => {
    cookieService.clear();
  },
  all: () => {
    clearAll.local();
    clearAll.session();
    clearAll.cookies();
  }
};

export default {
  setItem,
  getItem,
  removeItem,
  getAll,
  clearAll
};
